import moment from 'moment';

export interface LocationTimezone {
  timeZoneId: string;
  timeZoneName: string;
  rawOffset: number;
  dstOffset: number;
}

export async function getTimezoneFromCoordinates(
  lat: number,
  lng: number,
  apiKey: string,
  timestamp?: number
): Promise<LocationTimezone | null> {
  try {
    const ts = timestamp || Math.floor(Date.now() / 1000);
    const url = `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${ts}&key=${apiKey}`;
    
    
    const response = await fetch(url);
    const data = await response.json();
    
    
    if (data.status === 'OK') {
      const timezone = {
        timeZoneId: data.timeZoneId,
        timeZoneName: data.timeZoneName,
        rawOffset: data.rawOffset,
        dstOffset: data.dstOffset
      };
      return timezone;
    } else {
      console.error('Timezone API error:', data.status, data.errorMessage);
      return null;
    }
  } catch (error) {
    console.error('Error fetching timezone:', error);
    return null;
  }
}

export function createDateTimeInLocationTimezone(
  date: string,
  time: string,
  locationTimezone: LocationTimezone
): string {
  try {
    const datePart = moment(date).format('YYYY-MM-DD');
    const dateTimeString = `${datePart}T${time}:00`;
    
    const utcDateTime = moment.utc(dateTimeString);
    
    const totalOffsetSeconds = locationTimezone.rawOffset + locationTimezone.dstOffset;
    const totalOffsetMinutes = totalOffsetSeconds / 60;
    
    const finalUtcDateTime = utcDateTime.clone().subtract(totalOffsetMinutes, 'minutes');
    
    
    return finalUtcDateTime.toISOString();
  } catch (error) {
    console.error('Error creating datetime in location timezone:', error);
    return moment(`${moment(date).format('YYYY-MM-DD')}T${time}:00`).utc().toISOString();
  }
}

export function displayTimeInLocationTimezone(
  utcDateTime: string,
  locationTimezone: LocationTimezone,
  format: string = 'HH:mm'
): string {
  try {
    const utcMoment = moment.utc(utcDateTime);
    
    const totalOffsetSeconds = locationTimezone.rawOffset + locationTimezone.dstOffset;
    const totalOffsetMinutes = totalOffsetSeconds / 60;
    
    const localMoment = utcMoment.clone().add(totalOffsetMinutes, 'minutes');
    
    return localMoment.format(format);
  } catch (error) {
    console.error('Error displaying time in location timezone:', error);
    return moment(utcDateTime).format(format);
  }
}


export async function getTimezoneFromLocationString(
  locationString: string,
  apiKey: string
): Promise<LocationTimezone | null> {
  try {
    
    const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(locationString)}&key=${apiKey}`;
    
    const geocodeResponse = await fetch(geocodeUrl);
    const geocodeData = await geocodeResponse.json();
    
    
    if (geocodeData.status === 'OK' && geocodeData.results.length > 0) {
      const location = geocodeData.results[0].geometry.location;
      
      const timezone = await getTimezoneFromCoordinates(location.lat, location.lng, apiKey);
      
      return timezone;
    } else {
      console.error('Geocoding error:', geocodeData.status, geocodeData.error_message);
      return null;
    }
  } catch (error) {
    console.error('Error getting timezone from location string:', error);
    return null;
  }
}

export function serializeTimezone(timezone: LocationTimezone): string {
  return JSON.stringify(timezone);
}

export function deserializeTimezone(timezoneString: string): LocationTimezone | null {
  try {
    if (!timezoneString) return null;
    const parsed = JSON.parse(timezoneString);
    if (parsed && typeof parsed === 'object' && 
        parsed.timeZoneId && parsed.timeZoneName && 
        typeof parsed.rawOffset === 'number' && typeof parsed.dstOffset === 'number') {
      return parsed as LocationTimezone;
    }
    return null;
  } catch (error) {
    console.error('Error deserializing timezone:', error);
    return null;
  }
}